import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Clerk } from "@clerk/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Debug utility functions for manual testing
 */
export const debugUtils = {
  /**
   * Manually expire the current session (for testing)
   */
  async expireSession() {
    if (window.Clerk?.session) {
      try {
        await window.Clerk.signOut();
        console.log("🔓 Manually expired session");
      } catch (error) {
        console.error("❌ Failed to expire session:", error);
      }
    } else {
      console.log("❌ No Clerk session available to expire");
    }
  },

  /**
   * Get current auth status
   */
  async getAuthStatus() {
    const clerk = window.Clerk;
    if (!clerk) {
      console.log("❌ Clerk not available");
      return;
    }

    const session = clerk.session;
    const user = clerk.user;

    console.log("🔍 Current auth status:", {
      hasSession: !!session,
      sessionId: session?.id,
      hasUser: !!user,
      userId: user?.id,
      userEmail: user?.emailAddresses?.[0]?.emailAddress,
      isLoaded: clerk.loaded,
    });

    if (session) {
      try {
        const token = await session.getToken();
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          const expiry = new Date(payload.exp * 1000);
          console.log("🎫 Token details:", {
            expiresAt: expiry.toISOString(),
            timeUntilExpiry: expiry.getTime() - Date.now(),
          });
        }
      } catch (error) {
        console.error("❌ Failed to get token details:", error);
      }
    }
  },
};

// Make debug utils available globally for testing
if (typeof window !== "undefined") {
  (window as any).debugAuth = debugUtils;
}

// Extend Window interface for Clerk
declare global {
  interface Window {
    Clerk?: Clerk;
    debugAuth?: typeof debugUtils;
  }
}
